import { Team, User } from '@litlingo/client';
import { userImportFields } from 'constants/users';
import { addTeamsFromCell } from 'utils/teams';
import { v4 } from 'uuid';
import { read, utils } from 'xlsx';
import { userTypesToRoles } from './userRoles';

export type UserParseErrorType = 'missing-field' | 'unknown-field' | 'missing-header';
export type UserParseError = {
  name: string;
  error: { type: UserParseErrorType; field: string; value?: string };
};
export type WorkbookSheets = Record<string, string[][]>;

export const isUsersSheet = (data: string[][]): boolean => {
  if (!data?.length) return false;

  const headerRow = data[0].map((c) => c?.toLowerCase().trim()).join(' ');

  return Object.values(userImportFields).some(({ aliases }) =>
    aliases.some((a) => headerRow.includes(a))
  );
};

export const parseSingleUsersSheet = (
  data: string[][],
  teams: Team[]
): { users: User[]; errors: UserParseError[] } => {
  const colPositions: Record<string, number | null> = {
    name: null,
    email: null,
    roles: null,
    division: null,
    region: null,
    managementLevel: null,
    insight: null,
    prevent: null,
  };

  const errors: UserParseError[] = [];

  data[0]?.forEach((cell, idx) => {
    const text = cell?.toLowerCase().trim() ?? '';

    Object.entries(userImportFields).forEach(([key, { aliases }]) => {
      if (aliases.some((a) => text.includes(a))) {
        colPositions[key] = idx;
      }
    });
  });

  Object.keys(userImportFields).forEach((header) => {
    if (colPositions[header] === null) {
      errors.push({
        name: '',
        error: { type: 'missing-header', field: header },
      });
    }
  });

  const normalize = (v: string): string => v.toLowerCase().trim();

  const users: User[] = data
    .slice(1)
    .filter((row) => row.some((cell) => cell && cell.trim() !== ''))
    .map((row) => {
      const user: Partial<User> = {
        uuid: v4(),
        name: '',
        email: '',
        roles: [],
        teams: [],
        insight_active_email: false,
        insight_active_chat: false,
        prevent_active_email: false,
        prevent_active_chat: false,
      };

      if (colPositions.name !== null) {
        user.name = row[colPositions.name]?.trim();
      }

      if (colPositions.email !== null) user.email = row[colPositions.email]?.trim();

      if (colPositions.roles !== null) {
        const roles = row[colPositions.roles]?.split(',').map((role) => role.trim()) || [];
        const validRoles: string[] = [];
        const invalidRoles: string[] = [];

        roles.forEach((role) => {
          const mappedRoles = userTypesToRoles([role]);
          if (mappedRoles.length > 0) {
            validRoles.push(role);
          } else {
            invalidRoles.push(role);
          }
        });
        const rolesToAdd = userTypesToRoles(validRoles);
        user.roles = rolesToAdd;

        invalidRoles.forEach((role) => {
          errors.push({
            name: user.email || user.name || '',
            error: {
              type: 'unknown-field',
              field: 'roles',
              value: role,
            },
          });
        });
      }

      if (colPositions.division !== null)
        addTeamsFromCell(user, row[colPositions.division], 'division', teams, errors);

      if (colPositions.region !== null)
        addTeamsFromCell(user, row[colPositions.region], 'region', teams, errors);

      if (colPositions.managementLevel !== null)
        addTeamsFromCell(
          user,
          row[colPositions.managementLevel],
          'management-level',
          teams,
          errors
        );

      if (colPositions.insight !== null) {
        const values = row[colPositions.insight]?.split(',').map(normalize) || [];

        user.insight_active_email = values.includes('o365');
        user.insight_active_chat = values.includes('teams');
      }

      if (colPositions.prevent !== null) {
        const values = row[colPositions.prevent]?.split(',').map(normalize) || [];

        user.prevent_active_email = values.includes('outlook');
        user.prevent_active_chat = values.includes('teams');
      }

      if (!user.name) {
        errors.push({
          name: user.email || '',
          error: { type: 'missing-field', field: 'name' },
        });
      }

      return user as User;
    });

  return { users, errors };
};

export const parseUsersFromXLSXToJSON = (
  sheets: WorkbookSheets,
  teams: Team[]
): { users: User[]; errors: UserParseError[] } => {
  const users: User[] = [];
  const errors: UserParseError[] = [];

  Object.entries(sheets).forEach(([, data]) => {
    if (!isUsersSheet(data)) return;

    const result = parseSingleUsersSheet(data, teams);

    users.push(...result.users);
    errors.push(...result.errors);
  });

  return { users, errors };
};

export const loadAndParseXLSXToJSON = (
  file: File,
  callback: (sheets: WorkbookSheets) => void
): void => {
  const excelToJson = (e: ProgressEvent<FileReader>, rABS: boolean): void => {
    if (!e.target?.result) {
      callback({});
      return;
    }

    const bstr = e.target.result;
    const wb = read(bstr, { type: rABS ? 'binary' : 'array' });

    const sheets: WorkbookSheets = {};

    wb.SheetNames.forEach((sheetName) => {
      const ws = wb.Sheets[sheetName];
      sheets[sheetName] = utils.sheet_to_json(ws, {
        header: 1,
        blankrows: false,
      }) as string[][];
    });

    callback(sheets);
  };

  const reader = new FileReader();
  const rABS = !!reader.readAsBinaryString;

  reader.onload = (event: ProgressEvent<FileReader>): void => excelToJson(event, rABS);

  if (rABS) {
    reader.readAsBinaryString(file);
  } else {
    reader.readAsArrayBuffer(file);
  }
};

export default loadAndParseXLSXToJSON;
